import { Flag, MapPin, Radio, Users } from "lucide-react";
import { LayoutGroup, motion } from "motion/react";
import { useId } from "react";
import { BrowserFrame } from "@/components/blocks/frames/Frames";
import { QuestMap } from "@/components/blocks/scenes/QuestScene";
import { EASE, SPRING } from "@/components/motion/motion";
import { useSceneLoop } from "@/hooks/use-scene-loop";
import { QUESTOVERY_URL } from "@/lib/constants";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";

const QUESTOVERY_HOST = new URL(QUESTOVERY_URL).host;
const STEP_KEYS = ["a", "b", "c", "d"] as const;
const EDITOR_DURATIONS = [2400, 2400, 3200, 2400] as const;

/** Web editor: steps on the left, map and step settings on the right. */
export function EditorScene({ className }: { className?: string }) {
	const { dict } = useI18n();
	const editor = dict.scenes.editor;
	const { ref, index } = useSceneLoop(EDITOR_DURATIONS, { initial: 2 });
	const layoutId = useId();

	return (
		<div ref={ref} className={cn("w-full", className)}>
			<BrowserFrame
				host={QUESTOVERY_HOST}
				tag={dict.common.illustrationBadge}
				contentClassName="grid grid-cols-[38%_1fr] sm:grid-cols-[30%_1fr_30%] min-h-[17rem]"
			>
				<LayoutGroup id={layoutId}>
					<ol className="space-y-1 border-r p-3">
						<li className="mb-2 truncate px-2 text-[11px] font-semibold">
							{dict.scenes.quest.title}
						</li>
						{STEP_KEYS.map((key, stepIndex) => (
							<li
								key={key}
								className="relative flex items-center gap-2 rounded-lg px-2 py-2 text-[11px]"
							>
								{stepIndex === index ? (
									<motion.span
										layoutId="editor-step"
										transition={SPRING}
										className="absolute inset-0 rounded-lg border border-signal/60 bg-signal/10"
									/>
								) : null}
								<span className="relative font-mono text-[9px] text-muted-foreground">
									{stepIndex + 1}
								</span>
								<span className="relative truncate font-medium">
									{editor.steps[key]}
								</span>
							</li>
						))}
					</ol>
				</LayoutGroup>
				<div className="relative overflow-hidden">
					<QuestMap />
				</div>
				<div className="hidden space-y-3 border-l p-3 text-[11px] sm:block">
					<p className="font-semibold">
						{editor.steps[STEP_KEYS[index] ?? "c"]}
					</p>
					<p className="flex items-center gap-2 rounded-lg border px-2 py-1.5">
						<Flag className="size-3 text-signal-ink" />
						{editor.stepType}
					</p>
					<p className="flex items-center gap-2 rounded-lg border px-2 py-1.5">
						<MapPin className="size-3 text-signal-ink" />
						{editor.trigger}
					</p>
					<div>
						<p className="mb-1.5 text-muted-foreground">{editor.radius}</p>
						<span className="relative block h-1 rounded-full bg-foreground/10">
							<span className="absolute inset-y-0 left-0 w-2/5 rounded-full bg-signal" />
							<span className="absolute top-1/2 left-2/5 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-signal bg-background" />
						</span>
					</div>
					<span className="mt-4 flex h-7 items-center justify-center rounded-lg bg-foreground text-[10.5px] font-semibold text-background">
						{editor.publish}
					</span>
				</div>
			</BrowserFrame>
		</div>
	);
}

const TRACKING_DURATIONS = [2600, 2600, 3400] as const;
const TEAMS = [
	{ key: "a", progress: [5, 6, 8] },
	{ key: "b", progress: [4, 5, 6] },
	{ key: "c", progress: [2, 3, 4] },
] as const;

/** Live follow-up of the games being played. */
export function TrackingScene({ className }: { className?: string }) {
	const { dict } = useI18n();
	const tracking = dict.scenes.tracking;
	const quest = dict.scenes.quest;
	const { ref, index } = useSceneLoop(TRACKING_DURATIONS, { initial: 2 });

	return (
		<div ref={ref} className={cn("w-full", className)}>
			<BrowserFrame
				host={QUESTOVERY_HOST}
				tag={dict.common.illustrationBadge}
				contentClassName="p-4 sm:p-5"
			>
				<div className="mb-4 flex items-center justify-between">
					<p className="text-sm font-semibold">{tracking.title}</p>
					<span className="flex items-center gap-1.5 rounded-full bg-foreground px-2.5 py-1 text-[10px] font-semibold text-background">
						<Radio className="size-3 text-signal" />
						{tracking.live}
					</span>
				</div>
				<ul className="space-y-2.5">
					{TEAMS.map((team) => {
						const steps = team.progress[index] ?? 0;
						const done = steps >= 8;
						return (
							<li
								key={team.key}
								className="flex items-center gap-3 rounded-xl border px-3 py-2.5 text-[11.5px]"
							>
								<Users className="size-3.5 shrink-0 text-muted-foreground" />
								<span className="w-[34%] shrink-0 truncate font-medium">
									{quest.teams[team.key]}
								</span>
								<span className="h-1.5 flex-1 overflow-hidden rounded-full bg-foreground/[0.07]">
									<motion.span
										className="block h-full origin-left rounded-full bg-signal"
										initial={false}
										animate={{ scaleX: steps / 8 }}
										transition={{ duration: 0.8, ease: EASE }}
									/>
								</span>
								<span className="w-8 text-right font-mono tabular-nums">
									{steps}/8
								</span>
								<span
									className={cn(
										"hidden w-20 shrink-0 rounded-full px-2 py-0.5 text-center text-[10px] font-medium sm:block",
										done
											? "bg-signal text-signal-foreground"
											: "bg-foreground/[0.06] text-muted-foreground",
									)}
								>
									{done ? tracking.done : tracking.running}
								</span>
							</li>
						);
					})}
				</ul>
			</BrowserFrame>
		</div>
	);
}
